import React, { FormEvent, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useSearchEngine } from "../hooks";
import { ActiveSearchFilters } from "./ActiveSearchFilters";
import { useAppDispatch, useAppSelector } from "../store/store.hooks";
import {
  clearActiveFilters,
  selectIsLoading,
  selectSearchResult,
} from "../store/shared.reducer";
import { Facet } from "../shared.models";

interface Props {
  searchIcon: string;
}

export const SearchHeader: React.VFC<Props> = ({ searchIcon }) => {

  /*********************************
   * Variables
   *********************************/
  const isLoading = useAppSelector(selectIsLoading);
  const searchResult = useAppSelector(selectSearchResult);
  const dispatch = useAppDispatch();
  const searchField = useRef<HTMLInputElement>(null);
  const scopeSelect = useRef<HTMLSelectElement>(null);
  const pathName =
    typeof window !== "undefined" ? window.location.pathname : ""; // En tillfällig fix. Sökningen kommer inte att fungera utan javascript, eftersom vi behöver sökvägen till sidan.
  const [doSearch] = useSearchEngine(pathName + "/FilterStyrandeDokument");

  const [searchParams] = useSearchParams();
  const queryString = searchResult?.query.queryString;
  const searchFieldDefaultValue = queryString === "*" ? "" : queryString;
  const searchResultQueryForvaltning = new URLSearchParams(
    searchResult.query.parameters
  ).get("forvaltning");

  const scopeOptions: Facet[] = [];
  searchResult.orgFacets.forEach((facet) => {
    const query =
      new URLSearchParams(facet.query).get("forvaltning") ||
      searchResultQueryForvaltning ||
      "";
    const option = { ...facet, query };
    scopeOptions.push(option);
  });

  /*********************************
   * Functions
   *********************************/
  function onSearchSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    // Delete potential offset to reset pagination to page 1
    searchParams.delete("offset");
    searchParams.set("q", searchField.current.value || "*");

    // If we change scope, cleanup some params
    const scope = scopeSelect.current.value;
    if (scope !== searchResultQueryForvaltning) {
      searchParams.set("forvaltning", scope);
      searchParams.delete("org_path");
      dispatch(clearActiveFilters());
    }

    doSearch(searchParams);
  }

  const goToSearchResultList = () => {
    const searchResultList = document.getElementById("searchResultList");
    searchResultList.focus();
  };

  /*********************************
   * Render
   *********************************/
  return (
    <section
      id="searchHeader"
      aria-label="Sektion med sökfält, aktiva filter och sökningens avgränsning"
      className="vgr-searchservice__head"
    >
      <h1 id="h1id">Hitta styrande dokument</h1>
      <form method="get" action="" onSubmit={(e) => onSearchSubmit(e)}>
        <div className="search-header">
          <label className="sr-only search-header__label" htmlFor="main-search-input">
            <span className="search-header__label-text visually-hidden">
              Ange sökord</span>
          </label>
          <div className="search-header__input-group g-col-12 search-header__search-box-button-container search-control-block__search-box-button-container">
            <input
              name="q"
              id="main-search-input"
              type="search"
              placeholder="Vad letar du efter"
              autoComplete="off"
              defaultValue={searchFieldDefaultValue}
              ref={searchField}
              disabled={isLoading}
              className="search-header__input search__field "
            />
            <div className="search-header__select-wrapper">
              <label className="scope-select" >

                <select
                  ref={scopeSelect}
                  id="scope-select"
                  name={"forvaltning"}
                  disabled={isLoading}
                  className="search-header__select"
                  defaultValue={scopeOptions.find((x) => x.selected)?.query}
                >
                  {scopeOptions.map((x) => {
                    return (
                      <option
                        key={`scope_option_${x.displayName}`}
                        value={x.query}
                      >
                        {x.displayName}
                      </option>
                    );
                  })}
                </select>
              </label>
            </div>
            <button
              onClick={goToSearchResultList}
              type="button"
              className="hits-shortcut sr-only search-header__skip-link search-control-block__skip-link"
            >
              Gå direkt till träfflistan
            </button>
            <button className="search-header__button search__button" disabled={isLoading} type="submit">
              <svg width="16" height="16">
                <use href={searchIcon}></use>
              </svg>
              Sök
            </button>
          </div>
        </div>
        <ActiveSearchFilters />
      </form>
    </section>
  );
};